import { useContext, useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { failureOf, said } from '@/api/errors';
import { recordOf } from '@norum/contracts';
import { useAddMyTask, useElaborateRecord, useRecordElaborations, type ChatContext } from '@/api/hooks';
import { MeContext } from '@/app/MeContext';
import { useFrameMaybe } from '@/app/frame';
import { Plain } from '@/app/technical';
import { contextFor } from '@/chat/context';
import { Avatar, Button, Kicker } from './primitives';
import { useToast } from './Toast';

/**
 * What a record says about itself, wherever a record is listed (ADR-0155).
 *
 * A row that only has a name makes a person open it to learn what it is,
 * who brought it and what to do. This panel answers those before the click:
 * what it is, what it needs, why it is in front of this person, where it
 * came from and who brought it, who owns it, which agents are on it, who
 * knows more, and the best next step — or, as plainly, that nothing is
 * needed from them. It opens under the row as an accordion; the full record
 * is one press further.
 *
 * It is made of what the platform already holds. A builder (each feature's
 * `explain.ts`) turns a row it has already read into this shape; nothing
 * here asks a model, invents a reason, or reaches for a record the person
 * may not read. A field the platform does not hold is left out, or said to
 * be unrecorded; it is never guessed. The next step is a rule over the
 * record's state, written where the record is, so it can be read, tested
 * and argued with. "Ask your twin" is the person's own message in the side
 * chat, naming the record by its page (the chat is told identifiers, never
 * prose, §11).
 */

export type PartyKind = 'person' | 'agent' | 'twin' | 'department' | 'system';

/** Someone, or something, a record is tied to. */
export interface Party {
  name: string;
  kind: PartyKind;
  /** What they are for here: "leads Outbound", "sponsors this agent". */
  role?: string | undefined;
  /** Where their own page is, when they have one. */
  to?: string | undefined;
}

export interface StepAction {
  label: string;
  /** A link, or a press; a press does one thing the person asked for. */
  to?: string | undefined;
  run?: (() => void) | undefined;
  busy?: boolean | undefined;
  primary?: boolean | undefined;
}

/**
 * What to do, said so a person can do it. `tip` is the best next step, or
 * the plain statement that nothing is needed from them; `instead` is
 * another way, or what happens if they leave it.
 */
export interface NextStep {
  tip: string;
  instead?: string | undefined;
  actions?: StepAction[] | undefined;
}

export interface ItemContext {
  /** What it is, in a sentence or two. */
  summary: string;
  /** What it needs from whoever is looking: the ask, the gap, the decision. */
  need?: string | undefined;
  /** Why it is in front of them, and why now. */
  why?: string | undefined;
  /** What made it. `null`: the platform holds no record of that. */
  source?: { label: string; to?: string | undefined; when?: string | undefined } | null | undefined;
  broughtBy?: (Party & { when?: string | undefined }) | null | undefined;
  owner?: Party | null | undefined;
  /** Agents and people working on it now. */
  involved?: Party[] | undefined;
  /** Who to ask for more: the lead, the agent of the department that holds it. */
  knows?: Party[] | undefined;
  /** Anything else worth a line, as label and value. */
  facts?: Array<[label: string, value: ReactNode | null]> | undefined;
  next: NextStep;
  /** Where the full record is. */
  open?: { to: string; label?: string | undefined } | undefined;
  /** What the side chat is told this is about, when the address does not say. */
  about?: ChatContext | undefined;
  /** What to call it in a sentence ("task", "request"), and its name. */
  subject: { kind: string; title: string };
  /** Offer "Add to my tasks": what the task is called, and the page it came from. */
  task?: { title: string; from?: string | undefined } | undefined;
}

const KIND_WORDS: Record<PartyKind, string> = {
  person: 'Person',
  agent: 'Agent',
  twin: 'Twin',
  department: 'Department',
  system: 'The platform',
};

function PartyChip({ party }: { party: Party }) {
  // A person needs no label, and a name that already says what it is ("Otis
  // Agent", "The platform") is not said twice.
  const kindWord = party.kind === 'person' || party.name.toLowerCase().includes(KIND_WORDS[party.kind].toLowerCase()) ? '' : KIND_WORDS[party.kind];
  const body = (
    <>
      {(party.kind === 'person' || party.kind === 'agent' || party.kind === 'twin') && (
        <Avatar name={party.name} size={22} agent={party.kind === 'person' ? undefined : { label: KIND_WORDS[party.kind] }} />
      )}
      <span className="font-medium text-ink">{party.name}</span>
      <span className="text-meta">
        {kindWord}
        {party.role ? `${kindWord ? ' · ' : ''}${party.role}` : ''}
      </span>
    </>
  );
  return party.to ? (
    <Link to={party.to} className="inline-flex min-h-tap items-center gap-2 text-dense no-underline hover:underline">
      {body}
    </Link>
  ) : (
    <span className="inline-flex min-h-tap items-center gap-2 text-dense">{body}</span>
  );
}

function Parties({ parties }: { parties: Party[] }) {
  return (
    <span className="flex flex-wrap items-center gap-x-4">
      {parties.map((p) => (
        <PartyChip key={`${p.kind}:${p.name}`} party={p} />
      ))}
    </span>
  );
}

const NOT_RECORDED = <span className="text-meta">Not recorded</span>;

/** The rows of who and where, only those the record has. */
export function contextRows(c: ItemContext): Array<[string, ReactNode]> {
  const rows: Array<[string, ReactNode]> = [];
  if (c.source !== undefined) {
    rows.push([
      'Came from',
      c.source === null ? (
        NOT_RECORDED
      ) : (
        <span className="flex flex-wrap items-center gap-x-2">
          {c.source.to ? (
            <Link to={c.source.to} className="inline-flex min-h-tap items-center">
              {c.source.label}
            </Link>
          ) : (
            <span>{c.source.label}</span>
          )}
          {c.source.when && <span className="text-meta">{c.source.when}</span>}
        </span>
      ),
    ]);
  }
  if (c.broughtBy !== undefined) {
    rows.push([
      'Brought in by',
      c.broughtBy === null ? (
        NOT_RECORDED
      ) : (
        <span className="flex flex-wrap items-center gap-x-3">
          <PartyChip party={c.broughtBy} />
          {c.broughtBy.when && <span className="text-meta">{c.broughtBy.when}</span>}
        </span>
      ),
    ]);
  }
  if (c.owner !== undefined) rows.push(['Owner', c.owner === null ? <span className="text-meta">Nobody yet</span> : <PartyChip party={c.owner} />]);
  if (c.involved && c.involved.length > 0) rows.push(['Working on it', <Parties key="involved" parties={c.involved} />]);
  if (c.knows && c.knows.length > 0) rows.push(['Knows more', <Parties key="knows" parties={c.knows} />]);
  for (const [label, value] of c.facts ?? []) if (value !== null && value !== undefined && value !== '') rows.push([label, value]);
  return rows;
}

/** What is asked of the twin, in the person's own voice, ready for them to edit and send. */
export function askWords(subject: ItemContext['subject']): string {
  const name = subject.title.trim();
  return `What should I know about this ${subject.kind}${name ? `, “${name.slice(0, 120)}”` : ''}? Tell me where it came from, who owns it and who else knows about it, and what I should do next — or that nothing is needed from me.`;
}

/**
 * Whether the best next step already carries this very button: the same
 * place under the same words. The panel then shows it once, not a second
 * time beneath it (Readiness drew "Open the step" twice). A next step that
 * names a different act on the same page ("Answer or say no" beside "Open
 * the request") is a different thing to do, and both stay.
 */
function nextGoesTo(c: ItemContext, to: string, label: string): boolean {
  return (c.next?.actions ?? []).some((a) => a.to === to && a.label === label);
}

function ActionLink({ to, children, primary }: { to: string; children: ReactNode; primary?: boolean | undefined }) {
  return (
    <Link
      to={to}
      className={`inline-flex min-h-tap min-w-tap items-center justify-center gap-2 rounded-brand border px-[22px] text-label uppercase tracking-button no-underline transition-colors duration-150 ease-brand ${
        primary ? 'border-[var(--btn-bg)] bg-[var(--btn-bg)] text-[var(--btn-fg)] hover:bg-[var(--btn-bg-hover)]' : 'border-control text-secondary hover:border-strong hover:text-ink'
      }`}
    >
      {children}
    </Link>
  );
}

/**
 * "Ask the agent to elaborate" (ADR-0165): the department's agent writes
 * what it makes of this record — what it is, what is missing, what to do
 * next — for the person who pressed, and nobody else. It reads what the side
 * chat reads for the record, by its identifiers; it changes nothing. A held
 * one is said, never shown.
 */
function Elaborate({ about }: { about: ChatContext }) {
  const elaborate = useElaborateRecord();
  // While one is on its way the list says so, and polls itself until it lands.
  const list = useRecordElaborations(about, true);
  const toast = useToast();
  const waiting = list.data?.pending ?? false;
  const latest = list.data?.elaborations[0];
  return (
    <div className="flex flex-col gap-2" data-testid="record-elaboration">
      <div>
        <Button
          disabled={elaborate.isPending || waiting}
          onClick={() =>
            elaborate.mutate(
              { context: about },
              {
                onSuccess: (r) => toast({ text: r.existing ? 'The agent is already on it; it appears here in a moment.' : 'Asked the agent to elaborate. It appears here in a moment.' }),
                onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
              },
            )
          }
        >
          {waiting ? 'Elaborating…' : latest ? 'Ask again' : 'Ask the agent to elaborate'}
        </Button>
      </div>
      {latest && (
        <div className="flex flex-col gap-1 border-l-2 border-accent pl-3">
          <p className="m-0 text-micro text-meta">
            <span className="font-medium text-ink">{latest.writtenByName}</span> elaborated this for you · {new Date(latest.createdAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </p>
          {latest.held || latest.body === null ? (
            <p className="m-0 text-dense italic text-meta">Held by the scanner: kept, not shown, and never read by an agent.</p>
          ) : (
            <p className="m-0 whitespace-pre-wrap text-dense">{latest.body}</p>
          )}
          <p className="m-0 text-micro text-meta">Only you see this. It changes nothing on the record; what to do about it is yours.</p>
        </div>
      )}
    </div>
  );
}

function AddToMyTasks({ task }: { task: NonNullable<ItemContext['task']> }) {
  const add = useAddMyTask();
  const toast = useToast();
  return (
    <Button
      disabled={add.isPending}
      onClick={() =>
        add.mutate(
          { title: task.title, ...(task.from ? { from: task.from } : {}) },
          {
            onSuccess: (r) => toast({ text: r.existing ? 'It is already one of your open tasks, so it is not added twice.' : 'Added to your tasks.' }),
            onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }),
          },
        )
      }
    >
      {add.isPending ? 'Adding…' : 'Add to my tasks'}
    </Button>
  );
}

/**
 * The panel itself. It stands where it is put: under a row, in a card, on
 * a record's page. Every part reads as words; ids and codes are for the
 * superadmin's own screens and are never built into this.
 */
export function ContextPanel({
  context: c,
  id,
  acts,
}: {
  context: ItemContext;
  id?: string | undefined;
  /**
   * The record's own one-press acts, where a screen has them (Today's row:
   * postpone, pin, delegate, mark done). They sit under the next step they
   * carry out, once — never a second copy of a button the row also shows.
   */
  acts?: ReactNode;
}) {
  const frame = useFrameMaybe();
  const me = useContext(MeContext);
  const rows = contextRows(c);
  const route = c.open?.to;
  const about = c.about ?? (route ? contextFor(route, me?.department ?? '') : undefined);
  const twin = frame?.twin ?? null;
  // Offered only where the server will take it: a person in the shell, on a
  // record the side chat can name. A task has its own, in its discussion,
  // which its people share and may adopt as the description (ADR-0149).
  const record = about ? recordOf(about) : null;
  const elaborates = me !== null && record !== null && record.kind !== 'task';
  return (
    // The panel answers to the room it is given, not the window's: a card on a
    // four-column board, the chat rail and a full page are all this component.
    <div className="@container">
    <section
      id={id}
      aria-label={`About ${c.subject.title}`}
      data-testid="item-context"
      className="flex flex-col gap-4 border border-line bg-sunken p-3 text-left @md:p-4"
      // A row's own click must not fire from inside what it opened.
      onClick={(e) => e.stopPropagation()}
    >
      <p className="m-0 text-dense text-ink">
        <Plain>{c.summary}</Plain>
      </p>
      {(c.need || c.why) && (
        <div className="grid grid-cols-1 gap-4 @xl:grid-cols-2">
          {c.need && (
            <div className="flex flex-col gap-1">
              <Kicker>What it needs</Kicker>
              <p className="m-0 text-dense text-secondary">
                <Plain>{c.need}</Plain>
              </p>
            </div>
          )}
          {c.why && (
            <div className="flex flex-col gap-1">
              <Kicker>Why it is here</Kicker>
              <p className="m-0 text-dense text-secondary">
                <Plain>{c.why}</Plain>
              </p>
            </div>
          )}
        </div>
      )}
      {rows.length > 0 && (
        <dl className="m-0 grid grid-cols-1 gap-x-4 gap-y-0.5 @md:grid-cols-[minmax(0,120px)_1fr]">
          {rows.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="flex items-center pt-2 text-micro uppercase tracking-label text-meta @md:min-h-tap @md:pt-0">{label}</dt>
              <dd className="m-0 flex min-h-tap flex-wrap items-center text-dense [overflow-wrap:anywhere]">{value}</dd>
            </div>
          ))}
        </dl>
      )}
      <div className="flex flex-col gap-2 border-l-2 border-accent bg-page p-3" data-testid="next-step">
        <Kicker tone="accent">Best next step</Kicker>
        <p className="m-0 text-dense font-medium text-ink">
          <Plain>{c.next.tip}</Plain>
        </p>
        {c.next.instead && (
          <p className="m-0 text-dense text-secondary">
            <Plain>{c.next.instead}</Plain>
          </p>
        )}
        {c.next.actions && c.next.actions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {c.next.actions.map((a) =>
              a.to ? (
                <ActionLink key={a.label} to={a.to} primary={a.primary}>
                  {a.label}
                </ActionLink>
              ) : (
                <Button key={a.label} variant={a.primary ? 'primary' : 'quiet'} onClick={a.run} disabled={a.busy}>
                  {a.label}
                </Button>
              ),
            )}
          </div>
        )}
        {acts && <div className="flex flex-col gap-2 border-t border-line pt-2">{acts}</div>}
      </div>
      <div className="flex flex-wrap gap-2">
        {c.open && !nextGoesTo(c, c.open.to, c.open.label ?? 'Open the full record') && <ActionLink to={c.open.to}>{c.open.label ?? 'Open the full record'}</ActionLink>}
        {c.task && <AddToMyTasks task={c.task} />}
        {frame && (
          <Button onClick={() => frame.ask(askWords(c.subject), about)}>{twin ? `Ask ${twin} about this` : 'Ask in the chat'}</Button>
        )}
      </div>
      {elaborates && about && <Elaborate about={about} />}
    </section>
    </div>
  );
}

/**
 * The disclosure a list row carries: a button that opens the panel under
 * it. For a list that is not a `Table`; a `Table` takes `explain` and draws
 * this itself.
 */
export function DetailsButton({
  open,
  onToggle,
  controls,
  label,
}: {
  open: boolean;
  onToggle: () => void;
  controls?: string | undefined;
  /** What it opens, for a reader who cannot see the row: the record's name. */
  label: string;
}) {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={controls}
      aria-label={`${open ? 'Hide' : 'Show'} details: ${label}`}
      title={open ? 'Hide details' : 'What is this? Who brought it, and what next'}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      onKeyDown={(e) => e.stopPropagation()}
      className="inline-flex min-h-tap min-w-tap shrink-0 items-center justify-center border border-transparent text-meta transition-colors duration-150 ease-brand hover:border-control hover:text-ink"
    >
      <span aria-hidden className="text-dense">
        {open ? '▾' : '▸'}
      </span>
    </button>
  );
}

/**
 * A record's details, as one control and the panel under it: for a card, a
 * row of a list the platform did not draw as a table. Closed until asked.
 */
export function Explainer({
  context,
  label,
  startOpen = false,
}: {
  context: ItemContext;
  label?: string | undefined;
  startOpen?: boolean | undefined;
}) {
  const [open, setOpen] = useState(startOpen);
  const id = useId();
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex min-h-tap items-center gap-2 self-start text-label uppercase tracking-button text-secondary hover:text-ink"
      >
        <span aria-hidden>{open ? '▾' : '▸'}</span>
        {label ?? (open ? 'Hide details' : 'What is this?')}
      </button>
      {open && <ContextPanel context={context} id={id} />}
    </div>
  );
}
